'use client'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

interface TeamFilterBarProps {
  onFilterChange?: (filters: TeamFilterState) => void
}

interface TeamFilterState {
  industryTag: string
  city: string
}

// 与数据库 team_industries.industry_tag 编码一致（-1 表示全部）
const INDUSTRY_TAGS = [
  { value: '-1', label: '全部' },
  { value: '612', label: '企业服务(SaaS)' },
  { value: '632', label: '政务服务' },
  { value: '625', label: '生活服务' },
  { value: '609', label: '人工智能' },
  { value: '613', label: '物联网' },
  { value: '628', label: '工业互联网' },
  { value: '616', label: '内容平台' },
  { value: '604', label: '在线教育' },
  { value: '602', label: '企业内部管理' },
  { value: '610', label: '物流仓储' },
  { value: '626', label: '汽车' },
  { value: '605', label: '金融' },
  { value: '608', label: '医疗健康' },
  { value: '631', label: '零售/新消费' },
  { value: '601', label: '电商' },
  { value: '603', label: '社交' },
  { value: '606', label: '旅游' },
  { value: '607', label: '云计算' },
  { value: '611', label: '外卖跑腿' },
  { value: '614', label: 'VR/AR' },
  { value: '615', label: '区块链' },
  { value: '617', label: '出行' },
  { value: '618', label: '安全' },
  { value: '619', label: '人力资源/HR' },
  { value: '620', label: '大数据' },
  { value: '621', label: '广告营销' },
  { value: '622', label: '农业' },
  { value: '623', label: '搜索' },
  { value: '624', label: '音视频' },
  { value: '627', label: '游戏/电竞' },
  { value: '629', label: '智慧数字孪生' },
  { value: '630', label: '能源' },
  { value: '633', label: '房地产' }
]

// 与数据库 team_basics.city 行政区划代码一致（-1 表示全部）
const CITY_TAGS = [
  { value: '-1', label: '全部' },
  { value: '110100', label: '北京' },
  { value: '310100', label: '上海' },
  { value: '440300', label: '深圳' },
  { value: '510100', label: '成都' },
  { value: '330100', label: '杭州' },
  { value: '320100', label: '南京' },
  { value: '440100', label: '广州' },
  { value: '420100', label: '武汉' },
  { value: '410100', label: '郑州' },
  { value: '610100', label: '西安' },
  { value: '500000', label: '重庆' },
  { value: '520100', label: '贵阳' },
  { value: '340100', label: '合肥' },
  { value: '370100', label: '济南' },
  { value: '120100', label: '天津' },
  { value: '320500', label: '苏州' },
  { value: '130100', label: '石家庄' },
  { value: '350200', label: '厦门' },
  { value: '430100', label: '长沙' },
  { value: '370200', label: '青岛' },
  { value: '530100', label: '昆明' },
  { value: '210100', label: '沈阳' }
]

export function TeamFilterBar({ onFilterChange }: TeamFilterBarProps) {
  const [industryOpen, setIndustryOpen] = useState(false)
  const [cityOpen, setCityOpen] = useState(false)
  const [selectedIndustry, setSelectedIndustry] = useState('-1')
  const [selectedCity, setSelectedCity] = useState('-1')

  const handleIndustrySelect = (value: string) => {
    setSelectedIndustry(value)
    setIndustryOpen(false)
    onFilterChange?.({ industryTag: value, city: selectedCity })
  }

  const handleCitySelect = (value: string) => {
    setSelectedCity(value)
    setCityOpen(false)
    onFilterChange?.({ industryTag: selectedIndustry, city: value })
  }

  const handleReset = () => {
    setSelectedIndustry('-1')
    setSelectedCity('-1')
    onFilterChange?.({ industryTag: '-1', city: '-1' })
  }

  const selectedIndustryLabel =
    INDUSTRY_TAGS.find(t => t.value === selectedIndustry)?.label || '全部'
  const selectedCityLabel =
    CITY_TAGS.find(t => t.value === selectedCity)?.label || '全部'

  return (
    <div className="flex flex-wrap items-center gap-4">
      {/* Industry Tag Filter */}
      <div className="relative">
        <button
          onClick={() => setIndustryOpen(!industryOpen)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          aria-haspopup="listbox"
          aria-expanded={industryOpen}
        >
          <span className="text-sm text-gray-700">行业标签</span>
          <span className="text-sm text-gray-500">: {selectedIndustryLabel}</span>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>

        {industryOpen && (
          <div className="absolute z-100 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-96 overflow-y-auto">
            {INDUSTRY_TAGS.map(tag => (
              <button
                key={tag.value}
                onClick={() => handleIndustrySelect(tag.value)}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                  selectedIndustry === tag.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
              >
                {tag.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* City Filter */}
      <div className="relative">
        <button
          onClick={() => setCityOpen(!cityOpen)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          aria-haspopup="listbox"
          aria-expanded={cityOpen}
        >
          <span className="text-sm text-gray-700">城市</span>
          <span className="text-sm text-gray-500">: {selectedCityLabel}</span>
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>

        {cityOpen && (
          <div className="absolute z-100 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-96 overflow-y-auto">
            {CITY_TAGS.map(tag => (
              <button
                key={tag.value}
                onClick={() => handleCitySelect(tag.value)}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                  selectedCity === tag.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                }`}
              >
                {tag.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Reset Button */}
      {(selectedIndustry !== '-1' || selectedCity !== '-1') && (
        <button
          onClick={handleReset}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          重置筛选
        </button>
      )}
    </div>
  )
}
